import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import { DAZE } from '../../../constants/measure';
import { DAZE_DISCONTINUE_RULE, DISCONTINUE_TIMER } from '../../../constants/time';
import AudioContainer from '../../../common/audio/audioContainer';
import PassageLayout from '../../../common/layouts/passageLayout';
import ClozeText from '../../../common/components/ClozeText';
import GoButton from '../../../common/components/GoButton';
import TextPaneScroll from '../../../common/textPaneScroll/TextPaneScroll';
import TimedView from '../../../common/timer/TimedView';
import ScreenErrorContainer from '../../../common/error/ScreenErrorContainer';
import { getScrollStepSize } from '../../dazeUtils';
import withBlockedBackNavigation from '../../../common/utils/withBlockedBackNavigation';
import { ApolloContext } from '../../../ApolloContextProvider';
import ConfirmPopup from '../../../common/components/ConfirmPopup';
import { ARIA_LABELS } from '../../../constants/text';


class DazePassageView extends TimedView {
    constructor(props) {
        super(props);
        this.getScrollableContent = this.getScrollableContent.bind(this);
        this.setUpTimer = this.setUpTimer.bind(this);
        this.onRetry = this.onRetry.bind(this);
        this.isTimerDisabled = this.isTimerDisabled.bind(this);
        this.isDiscontinued = false;
        this.toggleConfirmPopup = this.toggleConfirmPopup.bind(this);
        this.onConfirm = this.onConfirm.bind(this);
        this.state = {
            showPopup: false,
        };
    }

    isTimerDisabled() {
        return this.context.isMazeUntimed &&
            this.context.isLouisianaLicenseAvailable &&
            this.props.flowStrategy &&
            this.props.flowStrategy.supportsUntimedAssessment;
    }

    componentDidMount() {
        if (!this.props.isDataFetched) {
            this.props.fetchContent();
        } else {
            this.props.initializePassage();
            this.setUpTimer();
            this.props.setIsMazeUntimed(this.context.isMazeUntimed);
        }
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.isNextButtonActive && this.props.isNextButtonActive) {
            this.scrollableView.scrollDown();
        }

        if (!prevProps.isDataFetched && this.props.isDataFetched) {
            this.props.initializePassage();
            this.setUpTimer();
        }
    }

    componentWillUnmount() {
        this.resetTimers();
        this.props.cleanup();
    }

    setUpTimer() {
        if (!this.isTimerDisabled()) {
            const discontinueDaze = () => {
                this.isDiscontinued = true;
                this.props.onNext(this.isDiscontinued);
            };
            this.scheduleEvent(DISCONTINUE_TIMER, discontinueDaze, DAZE_DISCONTINUE_RULE.TIME);
        }
    }

    onRetry() {
        this.props.onNext(this.isDiscontinued, true);
    }

    toggleConfirmPopup(val) {
        this.setState({ showPopup: val });
    }

    getScrollableContent() {
        const titleSegment = (<h1 key="text-title">{this.props.title}</h1>);
        const textSegment = (
            <div className="lined-text">
                {this.props.paragraphs.map((paragraph) => (
                    <ClozeText
                        isTabIndexActive={!this.props.isScreenBlocked && !this.props.isGreyedOut}
                        key={paragraph.id}
                        text={paragraph.text}
                        questions={this.props.questions}
                        selectedResponses={this.props.selectedResponses}
                        onResponseSelected={this.props.onResponseSelected} />
                ))}
            </div>
        );
        const buttonSegment = (this.props.isDataFetched && this.isTimerDisabled()) || this.props.isNextButtonActive
            ? (
                <GoButton
                    isEnabled={!this.props.isScreenBlocked}
                    disabled={this.props.isOkButtonGreyedOut}
                    onClick={() => this.isTimerDisabled() ? this.toggleConfirmPopup(true) : this.props.onNext()}
                    ariaLabel={ARIA_LABELS.DONE}
                />
            ) : null;
        return [titleSegment, textSegment, buttonSegment];
    }

    onConfirm() {
        this.props.onNext();
        this.toggleConfirmPopup(false);
    }

    render() {
        return (
            <PassageLayout
                class={classNames(`${DAZE}-measure`, { 'is-extended-view': this.props.isExtendedView })}
                isScreenBlocked={this.props.isScreenBlocked}
                isGreyedOut={this.props.isGreyedOut}>
                <ScreenErrorContainer onRetry={this.onRetry} />
                <AudioContainer />
                {this.state.showPopup && (
                    <ConfirmPopup
                        title="Finish Assessment"
                        message="Are you sure you’re done?"
                        confirmText="Yes, I’m done!"
                        cancelText="No, I’m still working"
                        onConfirm={this.onConfirm}
                        onCancel={() => this.toggleConfirmPopup(false)}
                    />
                )}
                <div className="text-pane">
                    <div className="daze-text">
                        <div className="content">
                            <TextPaneScroll
                                ref={(node) => {
                                    this.scrollableView = node;
                                }}
                                scrollToSection="segment-part-one"
                                scrollStep={getScrollStepSize(this.props.isExtendedView, this.context.fontSizeScaleValue)}
                                isEnabled={this.props.isDataFetched && this.props.isScrollEnabled}
                                content={this.getScrollableContent()}
                                isTabIndexDisabled={this.props.isGreyedOut || this.props.isScreenBlocked}
                            />
                        </div>
                    </div>
                </div>
            </PassageLayout>
        );
    }
}

DazePassageView.propTypes = {
    isDataFetched: PropTypes.bool,
    isScreenBlocked: PropTypes.bool,
    isGreyedOut: PropTypes.bool,
    isOkButtonGreyedOut: PropTypes.bool,
    isNextButtonActive: PropTypes.bool,
    isScrollEnabled: PropTypes.bool,
    title: PropTypes.string,
    paragraphs: PropTypes.arrayOf(PropTypes.shape({
        text: PropTypes.string.isRequired,
        id: PropTypes.string.isRequired,
    })),
    questions: PropTypes.array,
    selectedResponses: PropTypes.object,
    onResponseSelected: PropTypes.func,
    fetchContent: PropTypes.func,
    onNext: PropTypes.func,
    cleanup: PropTypes.func,
    initializePassage: PropTypes.func,
    isExtendedView: PropTypes.bool,
    setIsMazeUntimed: PropTypes.func,
};

DazePassageView.defaultProps = {
    isDataFetched: false,
    isGreyedOut: false,
    isOkButtonGreyedOut: false,
    isScreenBlocked: false,
    isNextButtonActive: false,
    isScrollEnabled: false,
    title: '',
    paragraphs: [],
    questions: [],
    selectedResponses: {},
    onResponseSelected: () => {},
    fetchContent: () => {},
    onNext: () => {},
    cleanup: () => {},
    initializePassage: () => { },
    isExtendedView: false,
    setIsMazeUntimed: () => {},
};

DazePassageView.displayName = 'DazePassageView';
DazePassageView.contextType = ApolloContext;

export { DazePassageView as DazePassageViewPure };
export default withBlockedBackNavigation(DazePassageView);
